import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import React, { useContext } from 'react';
import { HubType } from '../../../../../../core/src/api';
import { ActiveHubsWithColorContext, HubWithColorCode } from '../../../../contexts';
import { RelativePeriods } from '../../../../util/constant';
import { tooltipContainerStyles } from '../../daily-forecast/WeatherForecastPlot';
import {
  getTickValues,
  getXaxisInterval,
  getXMinFromPeriod,
  LINE_COLORS,
} from './LevelHistoryPlotHelpers';
import { MONTHS } from './TelemetryHistoryPlot';

declare global {
  interface Window {
    moment: any;
  }
}
window.moment = moment;

const useStyles = makeStyles({
  tooltipContainer: {
    ...tooltipContainerStyles,
  },
});

const formatHourTick = (dt) => `${dt.toFormat('h a')}<br/>${dt.month}/${dt.day}`;

const getFormatedDay = (dt: DateTime, as_of = DateTime.local(), prevent_now = false): string => {
  if (!prevent_now && dt.toMillis() <= as_of.toMillis()) {
    return 'Now';
  }
  if (dt.toMillis() >= DateTime.local(3000, 1, 1).toMillis()) {
    return 'Forever';
  }
  if (dt.hasSame(as_of, 'day')) {
    return 'Today';
  }
  return dt.toFormat('ccc');
};

const formatTimeTick = (dt) =>
  `${getFormatedDay(dt, DateTime.local(), true)}<br/>${dt.month}/${dt.day}`;

const getMinMaxValues = (series: any, x_min, x_max) => {
  if (series.length > 0) {
    let max = -Infinity;
    let min = Infinity;
    for (const points of series) {
      const pointsWithinPeriod = points.filter(({ x }) => x >= x_min && x <= x_max);
      for (const { y } of pointsWithinPeriod) {
        if (y > max) max = y;
        if (y < min) min = y;
      }
    }
    return [min, max];
  }
  return [0, 0];
};

export type TapeDropHistoryPlotProps = {
  period: string;
  grain_bin_location_timezone: string;
  // dual-point series: sidewall tape drop per hub
  active_sidewall_tape_drop_data: {
    hub_id: number;
    plotData: { x: number; y: number }[];
  }[];
  // dual-point series: center tape drop per hub
  active_center_tape_drop_data: {
    hub_id: number;
    plotData: { x: number; y: number }[];
  }[];
  // single-point series: eave tape drop per hub
  active_eave_tape_drop_data: {
    hub_id: number;
    plotData: { x: number; y: number }[];
  }[];
  active_grain_tickets_level_data: { x: number; y: number }[];
  max_height_ft: number;
};

const TapeDropHistoryPlot: React.FunctionComponent<TapeDropHistoryPlotProps> = ({
  period,
  grain_bin_location_timezone,
  active_sidewall_tape_drop_data,
  active_center_tape_drop_data,
  active_eave_tape_drop_data,
  active_grain_tickets_level_data,
  max_height_ft,
}) => {
  const activeHubsWithColorContext = useContext(ActiveHubsWithColorContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const isSmallMobile = useMediaQuery(theme.breakpoints.only('xs'), { noSsr: true });
  const activeHubsWithColor = activeHubsWithColorContext.activeHubsWithColor;
  const classes = useStyles();

  Highcharts.setOptions({ time: { timezone: grain_bin_location_timezone } });

  const today = DateTime.local();
  today.set({ hour: 12 });
  const tick_values = getTickValues(period, today.year, today.month, today.day, today.hour);

  const x_min =
    active_grain_tickets_level_data.length > 0
      ? active_grain_tickets_level_data[0].x
      : getXMinFromPeriod(period);
  const x_max =
    active_grain_tickets_level_data.length > 0
      ? active_grain_tickets_level_data[active_grain_tickets_level_data.length - 1].x
      : tick_values[tick_values.length - 1].toMillis();

  // Build sidewall tape drop series (dual-point)
  const sidewallSeriesData =
    activeHubsWithColor &&
    activeHubsWithColor.length > 0 &&
    active_sidewall_tape_drop_data
      .filter((row) => row.plotData && row.plotData.length > 0)
      .map(({ hub_id, plotData }) => {
        const hub: HubWithColorCode | undefined = activeHubsWithColor.find(
          (h) => h.hub_id === hub_id
        );
        if (!hub) return null;
        return {
          id: `sidewall-tape-drop-${hub.nickname}`,
          name: `Sidewall - ${hub.nickname}`,
          yAxis: 0,
          type: 'spline',
          data: plotData,
          color: LINE_COLORS[(hub.order + 1) % LINE_COLORS.length],
          zIndex: 999,
        };
      })
      .filter((d) => d != null);

  // Build center tape drop series (dual-point)
  const centerSeriesData =
    activeHubsWithColor &&
    activeHubsWithColor.length > 0 &&
    active_center_tape_drop_data
      .filter((row) => row.plotData && row.plotData.length > 0)
      .map(({ hub_id, plotData }) => {
        const hub: HubWithColorCode | undefined = activeHubsWithColor.find(
          (h) => h.hub_id === hub_id
        );
        if (!hub) return null;
        return {
          id: `center-tape-drop-${hub.nickname}`,
          name: `Center - ${hub.nickname}`,
          yAxis: 0,
          type: 'spline',
          data: plotData,
          color: hub.color,
          zIndex: 999,
        };
      })
      .filter((d) => d != null);

  // Build eave tape drop series (single-point)
  const eaveSeriesData =
    activeHubsWithColor &&
    activeHubsWithColor.length > 0 &&
    active_eave_tape_drop_data
      .filter((row) => row.plotData && row.plotData.length > 0)
      .map(({ hub_id, plotData }) => {
        const hub: HubWithColorCode | undefined = activeHubsWithColor.find(
          (h) => h.hub_id === hub_id
        );
        if (!hub) return null;
        return {
          id: `eave-tape-drop-${hub.nickname}`,
          name: `Eave Tape Drop - ${hub.nickname}`,
          yAxis: 0,
          type: 'spline',
          data: plotData,
          color: hub.color,
          zIndex: 999,
        };
      })
      .filter((d) => d != null);

  // Highcharts will not render axes when series is completely empty. Including a hidden
  // transparent series from grain tickets data ensures axes are always drawn even when
  // there is no tape drop sensor data. See CLOUD-1431.
  const grainTicketsHiddenSeriesData = {
    name: 'Grain Tickets',
    yAxis: 0,
    type: 'line',
    data: active_grain_tickets_level_data,
    enableMouseTracking: false,
    color: 'transparent',
    showInLegend: false,
  };

  const seriesData = [
    ...(centerSeriesData ? centerSeriesData : []),
    ...(sidewallSeriesData ? sidewallSeriesData : []),
    ...(eaveSeriesData ? eaveSeriesData : []),
    grainTicketsHiddenSeriesData,
  ];

  const allSeriesForMinMax = [
    ...(centerSeriesData ? centerSeriesData.map((s: any) => s.data) : []),
    ...(sidewallSeriesData ? sidewallSeriesData.map((s: any) => s.data) : []),
    ...(eaveSeriesData ? eaveSeriesData.map((s: any) => s.data) : []),
  ];

  const [finalMin, finalMax] =
    allSeriesForMinMax.length > 0
      ? getMinMaxValues(allSeriesForMinMax, x_min, x_max)
      : [0, max_height_ft];

  // Same tick interval logic as DualLevelHistoryPlot, anchored to max_height_ft
  const commonTickInterval = Math.round(
    finalMax <= max_height_ft * 0.4 ? max_height_ft * 0.1 : max_height_ft * 0.2
  );

  const yAxisMin = finalMin < 0 ? Math.floor(finalMin - commonTickInterval * 0.1) : 0;
  const yAxisMax =
    Math.abs(finalMax) <= commonTickInterval
      ? 2 * commonTickInterval
      : Math.ceil(finalMax / commonTickInterval) * commonTickInterval;

  const chartOptions = {
    credits: false,
    chart: {
      spacingTop: 8,
      applyLegendFix: true,
      height: 350,
      spacingLeft: isSmallMobile ? 0 : 10,
      spacingRight: isSmallMobile ? 0 : 10,
      alignTicks: false,
    },
    title: { text: '', y: 0 },
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: 'rgba(255,255,255,0)',
      borderWidth: 0,
      borderRadius: 0,
      shadow: false,
      style: { zIndex: 9999 },
      formatter() {
        const tooltipHeading =
          period === RelativePeriods.week || period === RelativePeriods.day
            ? Highcharts.dateFormat('%l:%M %p', this.x)
            : Highcharts.dateFormat('%e %b', this.x);
        const seriesdataPointInfo =
          this.points &&
          this.points
            .filter((category) => category.series.visible)
            .map((point) => {
              if (!point) return '';
              return (
                `<span style="color:${point.color};">\u25CF</span> ` +
                `<b>${point.y.toFixed(1)} ft</b><br/>`
              );
            })
            .filter((info) => info !== '')
            .join('');
        return `<div class="${classes.tooltipContainer}">
          <b>${tooltipHeading}</b><br/>${seriesdataPointInfo}
          </div>`;
      },
      followPointer: true,
      followTouchMove: true,
      stickOnContact: true,
      animation: false,
    },
    xAxis: {
      type: 'datetime',
      useHTML: true,
      labels: {
        style: { fontSize: 12, fontFamily: 'Source Sans Pro,sans-serif' },
        formatter() {
          const dt = DateTime.fromMillis(this.value);
          return {
            [RelativePeriods.day]: formatHourTick(dt),
            [RelativePeriods.week]: formatTimeTick(dt),
            [RelativePeriods.month]: `${MONTHS[dt.month - 1].substring(0, 3)} ${dt.day}`,
            [RelativePeriods.quarter]: MONTHS[dt.month - 1],
          }[period];
        },
      },
      min: x_min,
      max: x_max,
      tickInterval: getXaxisInterval(period, isMobile),
      crosshair: { enabled: true, color: 'blue' },
    },
    yAxis: [
      {
        // Single ft axis — left labels show inverted %, right labels show raw ft
        title: { text: null },
        min: yAxisMin,
        max: yAxisMax,
        tickInterval: commonTickInterval,
        plotLines: [{ color: 'gray', dashStyle: 'dot', width: 1, value: 0 }],
        labels: {
          formatter() {
            // 0 ft drop → 100% full, max_height_ft drop → 0% full
            return `${Math.round((1 - this.value / max_height_ft) * 100)}%`;
          },
          style: { fontSize: '12px', fontFamily: 'Source Sans Pro,sans-serif' },
        },
        gridLineWidth: 0,
      },
      {
        // Right axis: same scale, raw ft labels
        title: { text: null },
        min: yAxisMin,
        max: yAxisMax,
        tickInterval: commonTickInterval,
        labels: {
          formatter() {
            return `${this.value} ft`;
          },
          style: { fontSize: '12px', fontFamily: 'Source Sans Pro,sans-serif' },
        },
        gridLineWidth: 0,
        opposite: true,
        linkedTo: 0,
      },
    ],
    series: seriesData,
    legend: {
      useHTML: true,
      verticalAlign: 'top',
      alignColumns: false,
      itemMarginBottom: 2,
      itemStyle: {
        fontWeight: 600,
        fontFamily: 'Source Sans Pro,sans-serif',
        fontSize: '14px',
      },
    },
    plotOptions: {
      series: {
        turboThreshold: 6000,
        states: { inactive: { opacity: 1 } },
        marker: { enabledThreshold: 6 },
      },
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default TapeDropHistoryPlot;
